// Pure helpers for the project browser and the asset library: which media a
// project uses, sizes of folders and selections, and sorting table columns.

import { customFontAssetId } from './fonts';
import { parseExt } from './ext';

/** Anything shaped like a folder (project folders, media folders). */
export interface TreeNode {
  id: string;
  parentId: string;
}

/** `rootId` and every folder below it. */
export function subtreeIds(nodes: readonly TreeNode[], rootId: string): Set<string> {
  const out = new Set([rootId]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const n of nodes) if (!out.has(n.id) && out.has(n.parentId)) (out.add(n.id), (grew = true));
  }
  return out;
}

/** A clip's references to uploaded files: its media, a custom font, a LUT. */
export interface ClipMediaRef {
  assetId?: string | null;
  ext?: string | null;
  text?: { fontFamily: string } | null;
}

/** Distinct asset ids a set of clips uses, sorted (stable for storing). */
export function clipMediaIds(clips: Iterable<ClipMediaRef>): string[] {
  const ids = new Set<string>();
  for (const c of clips) {
    if (c.assetId) ids.add(c.assetId);
    const font = c.text ? customFontAssetId(c.text.fontFamily) : null;
    if (font) ids.add(font);
    const lut = c.ext ? parseExt(c.ext).lut?.assetId : undefined;
    if (lut) ids.add(lut);
  }
  return [...ids].sort();
}

/** Id of a project_asset row (a file a project uses). */
export const projectAssetId = (projectId: string, assetId: string) => `${projectId}:${assetId}`;

/** project_asset rows grouped by project: the files each project's clips use. */
export function mediaByProject(rows: Iterable<{ projectId: string; assetId: string }>): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const r of rows) {
    const list = out.get(r.projectId);
    if (list) list.push(r.assetId);
    else out.set(r.projectId, [r.assetId]);
  }
  return out;
}

/**
 * What to change so a project's usage rows match the files it uses now:
 * asset ids to add and existing ones to remove.
 */
export function diffMediaIds(current: Iterable<string>, wanted: Iterable<string>): { add: string[]; remove: string[] } {
  const want = new Set(wanted);
  const have = new Set(current);
  return { add: [...want].filter((id) => !have.has(id)).sort(), remove: [...have].filter((id) => !want.has(id)).sort() };
}

/** Sum of `sizeOf` over distinct ids (media shared by several projects counts once); unknown ids count 0. */
export function totalBytes(ids: Iterable<string>, sizeOf: (id: string) => number | undefined): number {
  let sum = 0;
  for (const id of new Set(ids)) sum += sizeOf(id) ?? 0;
  return sum;
}

/** The item with the largest time, or undefined for none. */
export function latestBy<T>(items: Iterable<T>, time: (item: T) => bigint | number): T | undefined {
  let best: T | undefined;
  let bestT: bigint | number = 0;
  for (const it of items) {
    const t = time(it);
    if (best === undefined || t > bestT) (best = it), (bestT = t);
  }
  return best;
}

/** Average bitrate in bits per second (0 when unknown). */
export function averageBitrate(sizeBytes: number, durationUs: number): number {
  return sizeBytes > 0 && durationUs > 0 ? (sizeBytes * 8) / (durationUs / 1e6) : 0;
}

export function formatBitrate(bps: number): string {
  if (!(bps > 0)) return '';
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(bps >= 1e8 ? 0 : 1)} Mbps`;
  return `${Math.round(bps / 1e3)} kbps`;
}

/** "16:9", "9:16", "4:5"… for a frame size; '' when unknown. Sizes close to a usual ratio get its name. */
export function aspectLabel(width: number, height: number): string {
  if (!(width > 0 && height > 0)) return '';
  const r = width / height;
  for (const k of ['16:9', '9:16', '1:1', '4:5', '5:4', '4:3', '3:4', '21:9', '2:1', '1.85:1', '2.39:1']) {
    const [a, b] = k.split(':').map(Number);
    if (Math.abs(r - a / b) / (a / b) < 0.01) return k;
  }
  const divisor = (x: number, y: number): number => (y ? divisor(y, x % y) : x);
  const [w, h] = [Math.round(width), Math.round(height)];
  const d = divisor(w, h);
  return `${w / d}:${h / d}`;
}

export type SortDir = 'asc' | 'desc';
export type SortValue = string | number | bigint | undefined | null;

/** Compares two column values: numbers by size, text naturally ("Cut 2" < "Cut 10"); empty values go last. */
export function compareValues(a: SortValue, b: SortValue): number {
  const ea = a === undefined || a === null || a === '';
  const eb = b === undefined || b === null || b === '';
  if (ea || eb) return ea === eb ? 0 : ea ? 1 : -1;
  if (typeof a === 'string' || typeof b === 'string') return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  return a! < b! ? -1 : a! > b! ? 1 : 0;
}

/**
 * Sorts rows by a column (stable: equal rows keep their order). Empty values
 * stay last in both directions.
 */
export function sortRows<T>(rows: readonly T[], value: (row: T) => SortValue, dir: SortDir): T[] {
  return rows
    .map((row, i) => ({ row, i, v: value(row) }))
    .sort((x, y) => {
      const empty = (v: SortValue) => v === undefined || v === null || v === '';
      if (empty(x.v) || empty(y.v)) return compareValues(x.v, y.v) || x.i - y.i;
      const c = compareValues(x.v, y.v);
      return (dir === 'asc' ? c : -c) || x.i - y.i;
    })
    .map((x) => x.row);
}

/**
 * A new file replacing an asset's original in place (the asset's
 * `replacement` column, JSON): clips, review links and the rest keep pointing
 * at the asset, which switches to the new file once it is processed.
 */
export interface AssetReplacement {
  /** Where the new original is stored ('' once it failed and was removed). */
  key: string;
  name: string;
  mime: string;
  ext: string;
  sizeBytes: number;
  status: 'uploading' | 'processing' | 'failed';
  error: string;
  userId: string;
  userName: string;
  /** Unix milliseconds. */
  startedAt: number;
}

/** The asset's pending (or failed) replacement, or null. */
export function parseReplacement(json: string | null | undefined): AssetReplacement | null {
  if (!json) return null;
  try {
    const r = JSON.parse(json);
    return r && typeof r === 'object' && typeof r.status === 'string' ? (r as AssetReplacement) : null;
  } catch {
    return null;
  }
}

/** Why `kind` media can't replace an asset of `assetKind`, or null when it can. */
export function replacementKindProblem(assetKind: string, kind: string | null): string | null {
  if (!kind) return 'Unsupported file type';
  if (kind === assetKind) return null;
  // Audio may come in a video container (an .mp4 with only sound); processing checks what is in it.
  if (assetKind === 'audio' && kind === 'video') return null;
  const what: Record<string, string> = { video: 'a video', audio: 'an audio file', image: 'an image', font: 'a font', lut: 'a LUT' };
  return `Replace ${what[assetKind] ?? 'this file'} with ${what[assetKind] ?? 'the same kind of file'}`;
}
